import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, LinearProgress, Breadcrumbs, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useDataProvider, useNotify, fetchUtils } from 'react-admin';
import { useNavigate } from 'react-router-dom';

interface WizardStep {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  completed?: boolean;
}

interface DriverProfileWizardFullscreenProps {
  steps: WizardStep[];
  onComplete?: () => void;
  initialStep?: number;
  driverId?: number;
}

export const DriverProfileWizardFullscreen: React.FC<DriverProfileWizardFullscreenProps> = ({
  steps,
  onComplete,
  initialStep = 0,
  driverId,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [stepData, setStepData] = useState<Record<string, any>>({});
  const [driver, setDriver] = useState<any>(null);

  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const progress = (completedSteps.size / steps.length) * 100;

  // Load existing driver data if editing
  useEffect(() => {
    if (driverId) {
      dataProvider
        .getOne('drivers', { id: driverId })
        .then(({ data }) => {
          setDriver(data);
          setStepData(prev => ({ ...prev, driver: data }));
        })
        .catch(error => {
          console.error('Error loading driver:', error);
        });
    }
  }, [driverId, dataProvider]);

  const saveStepData = async (stepId: string, data: any) => {
    try {
      if (stepId === 'driver' && data) {
        let result;
        if (driverId) {
          // Update existing driver
          result = await dataProvider.update('drivers', {
            id: driverId,
            data: data,
            previousData: driver,
          });
        } else {
          // Create new driver
          result = await dataProvider.create('drivers', { data });
          setDriver(result.data);
        }

        setStepData(prev => ({ ...prev, [stepId]: result.data }));
        notify('Driver profile saved successfully', { type: 'success' });
        return result.data;
      } else if (stepId === 'license' && data && driver?.id) {
        // Save driver license using custom endpoint
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');

        const options = {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          }),
          body: JSON.stringify({ ...data, driverId: driver.id }),
        };

        const response = await fetchUtils.fetchJson(`${baseUrl}/drivers/licenses`, options);
        setStepData(prev => ({ ...prev, [stepId]: response.json }));
        notify('License saved successfully', { type: 'success' });
        return response.json;
      } else if (stepId === 'education' && data && driver?.id) {
        // Save driver education using custom endpoint
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');

        const options = {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          }),
          body: JSON.stringify({ ...data, driverId: driver.id }),
        };

        const response = await fetchUtils.fetchJson(`${baseUrl}/drivers/education`, options);
        setStepData(prev => ({ ...prev, [stepId]: response.json }));
        notify('Education saved successfully', { type: 'success' });
        return response.json;
      } else if (stepId === 'employment' && data && driver?.id) {
        // Save driver employment
        const result = await dataProvider.create('driver-employment', {
          data: { ...data, driverId: driver.id },
        });
        setStepData(prev => ({ ...prev, [stepId]: result.data }));
        notify('Employment saved successfully', { type: 'success' });
        return result.data;
      } else if (stepId === 'certification' && data && driver?.id) {
        // Save driver certification - use dataProvider since this resource exists
        const result = await dataProvider.create('driver-certifications', {
          data: { ...data, driverId: driver.id },
        });
        setStepData(prev => ({ ...prev, [stepId]: result.data }));
        notify('Certification saved successfully', { type: 'success' });
        return result.data;
      }
    } catch (error: any) {
      notify('Error saving data: ' + (error.message || 'Unknown error'), { type: 'error' });
      throw error;
    }
  };

  const handleNext = async (data?: any) => {
    const currentStepId = steps[currentStep].id;

    try {
      // Save current step data if provided
      if (data) {
        const savedData = await saveStepData(currentStepId, data);
        if (currentStepId === 'driver' && savedData) {
          setDriver(savedData);
        }
      }

      // Mark step as completed
      setCompletedSteps(prev => new Set(prev).add(currentStep));

      // Move to next step or complete
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error saving step data:', error);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    navigate('/drivers');
  };

  const CurrentStepComponent = steps[currentStep]?.component;

  // Breadcrumb navigation
  const breadcrumbSteps = ['Profile Builder', steps[currentStep]?.title || ''];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb">
            {breadcrumbSteps.map((step, index) => (
              <Typography
                key={index}
                color={index === breadcrumbSteps.length - 1 ? 'primary' : 'text.primary'}
                sx={{ fontWeight: index === breadcrumbSteps.length - 1 ? 'bold' : 'normal' }}
              >
                {step}
              </Typography>
            ))}
          </Breadcrumbs>

          {/* Close button */}
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Progress bar */}
        <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Step {currentStep + 1} of {steps.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}% complete
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#1976d2',
              },
            }}
          />
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          maxWidth: 800,
          mx: 'auto',
          p: 4,
        }}
      >
        <Paper
          sx={{
            p: 4,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {CurrentStepComponent && (
            <CurrentStepComponent
              onNext={handleNext}
              onBack={handleBack}
              onClose={handleClose}
              existingData={stepData[steps[currentStep].id]}
              driver={driver}
              isFirstStep={currentStep === 0}
              isLastStep={currentStep === steps.length - 1}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};
